import type { JsonFetchOptions, ResponseError } from '../types'

import { isResponseError } from '../predicates'

import humps from 'humps'

import { camelizeJSON, parse } from './common'
import { raw } from './raw'

// Carries the status, so a caller can tell an unparseable body apart from a
// malformed success - an HTML error page, or the empty body raw() leaves behind
// when it answers a 401 with a Turbolinks reload rather than a rejection.
class JsonParseError extends Error implements ResponseError {
  readonly status: number
  readonly ok = false
  readonly statusText: string
  readonly body: string

  constructor(response: Response, body: string) {
    super(`Could not parse the response body (${response.status})`)

    this.name = 'JsonParseError'
    this.status = response.status
    this.statusText = response.statusText
    this.body = body
  }
}

const parseJSON = async <R>(response: Response): Promise<R> => {
  const text = await response.text()

  try {
    return JSON.parse(text) as R
  } catch {
    throw new JsonParseError(response, text)
  }
}

// Exported for the callers that need a JSON body but not a JSON response — they
// have to serialise it the way `json` does or the same payload reaches the
// server differently depending on which helper sent it.
export const decamelizeBody = (body: Record<string, unknown>): BodyInit => {
  const decamelizedBody = humps.decamelizeKeys(body)
  return JSON.stringify(decamelizedBody)
}

export const json = async <R>(
  url: URL | RequestInfo,
  options: JsonFetchOptions = {},
  preferences: { camelizeJSON: boolean } = { camelizeJSON: true }
): Promise<R | null> => {
  options.headers = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...options.headers,
  }

  const effectiveOptions = options.body
    ? {
        ...options,
        body: decamelizeBody(options.body),
      }
    : (options as RequestInit)

  const data = await raw(url, effectiveOptions)

  const parsedData = await parse<R>(parseJSON)(data)

  if (!parsedData) {
    return null
  }

  return preferences.camelizeJSON ? (camelizeJSON(parsedData) as R) : parsedData
}

export const jsonWithAlert = async <T>(
  url: URL | RequestInfo,
  options: JsonFetchOptions = {},
  preferences: { camelizeJSON: boolean } = { camelizeJSON: true }
): Promise<T | null | void> => {
  try {
    return await json<T>(url, options, preferences)
  } catch (e) {
    // We don't want to show alert for Browser error like cancelled requests
    if (isResponseError(e) && typeof e.body !== 'string' && e.status !== 0) {
      const errorMessage = Object.values(e?.body?.errors || []).join()
      if (errorMessage) {
        alert('Something went wrong: ' + errorMessage)
      }
    }
  }
}
